import {HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {inject} from '@angular/core';
import {AlertService} from './alert/alert.service';

const GENERIC_ERROR_MESSAGE = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';

/**
 * Set on a request whose caller shows its own, user-facing error message (e.g. the binding offer submit,
 * GE-1540). The error is still rethrown; only the global alert with the raw server message is skipped.
 */
export const SKIP_GLOBAL_ERROR_ALERT = new HttpContextToken<boolean>(() => false);

export function errorInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  const alertService = inject(AlertService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (req.context.get(SKIP_GLOBAL_ERROR_ALERT)) {
        return throwError(() => error);
      }

      let errorMessage: string;

      if (error.error instanceof ErrorEvent) {
        errorMessage = `Client-side error: ${error.error.message}`;
      } else {
        errorMessage = extractUserFriendlyMessage(error);
      }

      alertService.showAlert(errorMessage);

      return throwError(() => error);
    })
  );
}

function extractUserFriendlyMessage(error: HttpErrorResponse): string {
  const errors: unknown = error.error?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const formatted = errors
      .filter((e): e is string => typeof e === 'string')
      .map(formatFieldError)
      .join(' | ');
    return `Server-side error: ${error.status} ${formatted}`;
  }

  const message = error.error?.message;
  if (typeof message === 'string' && message.length > 0 && !isTechnicalMessage(message)) {
    return `Server-side error: ${error.status} ${message}`;
  }

  return GENERIC_ERROR_MESSAGE;
}

function formatFieldError(fieldError: string): string {
  const colonIdx = fieldError.indexOf(': ');
  if (colonIdx === -1) return fieldError;
  const field = fieldError.substring(0, colonIdx);
  const message = fieldError.substring(colonIdx + 2);
  const shortField = field.includes('.') ? field.substring(field.lastIndexOf('.') + 1) : field;
  return `${shortField}: ${message}`;
}

function isTechnicalMessage(message: string): boolean {
  return (
    message.includes('org.springframework') ||
    message.includes('java.lang') ||
    message.includes('net.syneco') ||
    message.includes('Validation failed for argument')
  );
}
